import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

type PeriodUnit = 'minutes' | 'hours';

export interface PeriodDisplayProps {
	value?: number; // Always in minutes
	className?: string;
	showUnit?: boolean; // Whether to show the unit label
}

/**
 * PeriodDisplay - Display-only period component
 *
 * - Reads values in minutes
 * - Displays in hours if the value is an exact multiple of 60, otherwise in minutes
 * - Formats the display nicely with unit labels
 */
export const PeriodDisplay: React.FC<PeriodDisplayProps> = ({
	value = 0,
	className,
	showUnit = true
}) => {
	const { t } = useTranslation();

	// Determine best unit for display
	const { displayValue, unit } = React.useMemo((): {
		displayValue: number;
		unit: PeriodUnit;
	} => {
		if (value === 0) {
			return { displayValue: 0, unit: 'minutes' };
		}

		if (value % 60 === 0) {
			return { displayValue: value / 60, unit: 'hours' };
		}

		return { displayValue: value, unit: 'minutes' };
	}, [value]);

	const unitLabel = React.useMemo(() => {
		if (unit === 'hours') {
			return displayValue === 1
				? t('common.hour', 'Hour')
				: t('common.hours', 'Hours');
		}
		return displayValue === 1
			? t('common.minute', 'Minute')
			: t('common.minutes', 'Minutes');
	}, [unit, displayValue, t]);

	return (
		<span className={cn('text-sm', className)}>
			{displayValue}
			{showUnit && <span className="ml-1 text-muted-foreground">{unitLabel}</span>}
		</span>
	);
};

PeriodDisplay.displayName = 'PeriodDisplay';
